import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import dayjs from "dayjs";
import TemplatePreview from "./TemplatePreview";
import ModalSalvarTemplate from "./ModalSalvarTemplate";
import ModalAplicarEmMassa from "./ModalAplicarEmMassa";
import InternoModalConfirmacaoSaida from "../../../../../../../components/modalSaindoDaPagina";
import { useAplicarEmMassaContext } from "../../../aplicarEmMassaContext";
import {
  deleteDiagramaTemplate,
  fetchDiagramaTemplates,
} from "./templatesService";
import { mergeEquipmentTypes } from "../equipmentTypes";
import {
  ensureCustomEquipmentTypesLoaded,
  getCustomEquipmentTypesSnapshot,
  subscribeCustomEquipmentTypes,
} from "../customEquipmentTypes/customEquipmentTypesStore";
import styles from "./styles";

// Modal de templates de diagrama: lista com filtro à esquerda e preview vivo à direita,
// permitindo renomear, excluir e aplicar (substituindo o diagrama) com decisão informada
export default function ModalTemplates({
  open = false,
  onClose = () => {},
  editMode = false,
  // "individual" = aplica no circuito aberto (DiagramaCanvas); "massa" = aplica em vários (serviceItem)
  modo = "individual",
  // equipmentTypes pode ser omitido: nesse caso o modal resolve internamente via store reativa
  equipmentTypes: equipmentTypesProp,
  onAplicar = () => {},
  setSnackbar = () => {},
}) {
  const theme = useTheme();
  const modalStyles = styles(theme);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  // Modal de aplicar o template selecionado em vários circuitos do cliente (Task 4)
  const [aplicarEmMassaOpen, setAplicarEmMassaOpen] = useState(false);
  // servicos/bloqueadoPorPendencia/aplicarTemplateEmMassa chegam via contexto para não enfiar
  // prop por DiagramaCanvas/diagramaEquipamentos/circuito, que não têm relação com essa feature
  const { servicos, bloqueadoPorPendencia, aplicarTemplateEmMassa } =
    useAplicarEmMassaContext();

  // Tipos de equipamento para o preview: usa prop se disponível, senão resolve via store reativa
  const customEquipmentTypes = useSyncExternalStore(
    subscribeCustomEquipmentTypes,
    getCustomEquipmentTypesSnapshot,
  );
  const equipmentTypesResolvido = useMemo(
    () => equipmentTypesProp || mergeEquipmentTypes(customEquipmentTypes),
    [equipmentTypesProp, customEquipmentTypes],
  );

  // Garante que tipos personalizados estejam carregados ao abrir o modal
  useEffect(() => {
    if (open && !equipmentTypesProp) {
      ensureCustomEquipmentTypesLoaded?.();
    }
  }, [open, equipmentTypesProp]);

  // Busca a lista sempre que o modal abre (templates mudam com pouca frequência)
  const carregarTemplates = async () => {
    setLoading(true);
    try {
      const lista = await fetchDiagramaTemplates();
      setTemplates(lista || []);
    } catch (error) {
      console.error("Erro ao buscar templates de diagrama:", error);
      setSnackbar?.({
        children: "Erro ao carregar templates.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Reseta estado local e recarrega a cada abertura
  useEffect(() => {
    if (!open) return;
    setFiltro("");
    setSelectedId(null);
    setRenameTarget(null);
    setDeleteConfirm(null);
    carregarTemplates();
  }, [open]);

  // Filtra por nome ou descrição conforme o usuário digita
  const templatesFiltrados = useMemo(() => {
    const termo = filtro?.trim?.()?.toLowerCase?.() || "";
    if (!termo) return templates;
    return (templates || []).filter((template) =>
      `${template?.nome || ""} ${template?.descricao || ""}`
        ?.toLowerCase?.()
        ?.includes?.(termo),
    );
  }, [templates, filtro]);

  const selectedTemplate = useMemo(
    () =>
      templatesFiltrados?.find((template) => template?.id === selectedId) ||
      null,
    [templatesFiltrados, selectedId],
  );

  // Exclui o template confirmado (hard delete) e recarrega a lista
  const handleDelete = async () => {
    const alvo = deleteConfirm;
    if (!alvo?.id) return;
    try {
      await deleteDiagramaTemplate(alvo?.id);
      setSnackbar?.({ children: "Template excluído.", severity: "success" });
      if (selectedId === alvo?.id) setSelectedId(null);
      await carregarTemplates();
    } catch (error) {
      console.error("Erro ao excluir template:", error);
      setSnackbar?.({
        children: "Erro ao excluir template.",
        severity: "error",
      });
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Aplica o template selecionado: fecha o modal e delega a decisão de confirmação ao canvas
  const handleAplicar = () => {
    if (!selectedTemplate || !editMode) return;
    const alvo = selectedTemplate;
    onClose?.();
    onAplicar?.(alvo);
  };

  // Abre o modal de aplicação em massa com o template atualmente selecionado na lista; fecha
  // este modal antes (ModalAplicarEmMassa é renderizado fora da Dialog de open para não ser
  // desmontado junto quando ela fecha — Dialog do MUI desmonta os filhos ao fechar)
  const handleAbrirAplicarEmMassa = () => {
    if (!selectedTemplate) return;
    onClose?.();
    setAplicarEmMassaOpen(true);
  };

  // Aplica o template em vários circuitos (Task 5): chama o service real via contexto (que já
  // reidrata `forms`) e propaga o resultado como snackbar com os números que o próprio backend
  // devolveu — nunca os estimados na tela, que podem estar desatualizados em relação ao banco
  const handleAplicarEmMassa = async ({ circuitoIds, novosCircuitos }) => {
    try {
      const resultado = await aplicarTemplateEmMassa?.({
        templateId: selectedTemplate?.id,
        circuitoIds,
        novosCircuitos,
      });
      setSnackbar?.({
        children:
          `${resultado?.substituidos || 0} circuito(s) tiveram o diagrama substituído · ` +
          `${resultado?.criados || 0} circuito(s) criado(s)` +
          (resultado?.anexosRemovidos
            ? ` · ${resultado.anexosRemovidos} anexo(s) removido(s)`
            : "") +
          ".",
        severity: "success",
      });
    } catch (error) {
      // Erro é tudo-ou-nada: a transação do backend não aplicou nada — relança para o modal de
      // seleção decidir permanecer aberto com a seleção intacta, em vez de fechar (Task 5, Step 4)
      console.error("Erro ao aplicar template em massa:", error);
      setSnackbar?.({
        children: "Erro ao aplicar o template em massa. Nada foi aplicado.",
        severity: "error",
      });
      throw error;
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, height: "82vh" } }}
    >
      <DialogTitle sx={modalStyles?.dialogTitle}>
        <Typography variant="h6" fontWeight="bold">
          Templates de diagrama
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Fechar">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={modalStyles?.dialogContent}>
        <Box sx={modalStyles?.listColumn}>
          <TextField
            label="Filtrar por nome ou descrição"
            size="small"
            fullWidth
            autoComplete="off"
            value={filtro}
            onChange={(evento) => setFiltro(evento?.target?.value || "")}
            sx={modalStyles?.filtroField}
          />
          {loading ? (
            <Box sx={modalStyles?.loadingBox}>
              <CircularProgress size={28} />
            </Box>
          ) : templatesFiltrados?.length ? (
            <Box sx={modalStyles?.listBox}>
              {templatesFiltrados.map((template) => {
                const isSelected = template?.id === selectedId;
                return (
                  <Box
                    key={template?.id}
                    sx={{
                      ...modalStyles?.itemBox,
                      ...(isSelected ? modalStyles?.itemBoxSelected : {}),
                    }}
                    onClick={() => setSelectedId(template?.id || null)}
                  >
                    <Box sx={modalStyles?.itemInfo}>
                      <Typography sx={modalStyles?.itemNome}>
                        {template?.nome}
                      </Typography>
                      {template?.descricao && (
                        <Typography sx={modalStyles?.itemDescricao}>
                          {template?.descricao}
                        </Typography>
                      )}
                      <Typography sx={modalStyles?.itemMeta}>
                        {`${template?.dados?.nodes?.length || 0} equip. · ${
                          template?.dados?.edges?.length || 0
                        } conexões · ${dayjs(template?.createdAt).format("DD/MM/YYYY")}`}
                      </Typography>
                    </Box>
                    <Box sx={modalStyles?.itemActions}>
                      <Tooltip title="Editar nome/descrição" arrow>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Editar template ${template?.nome}`}
                            onClick={(evento) => {
                              evento?.stopPropagation?.();
                              setRenameTarget(template);
                            }}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Excluir template" arrow>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Excluir template ${template?.nome}`}
                            onClick={(evento) => {
                              evento?.stopPropagation?.();
                              setDeleteConfirm(template);
                            }}
                          >
                            <DeleteOutlineOutlined fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography sx={modalStyles?.emptyText}>
              Nenhum template encontrado. Monte um diagrama e use "Salvar como
              template".
            </Typography>
          )}
        </Box>
        <Box sx={modalStyles?.detailColumn}>
          {selectedTemplate ? (
            <>
              <Box sx={modalStyles?.detailHeader}>
                <Typography sx={modalStyles?.detailNome}>
                  {selectedTemplate?.nome}
                </Typography>
                {selectedTemplate?.descricao && (
                  <Typography sx={modalStyles?.detailDescricao}>
                    {selectedTemplate?.descricao}
                  </Typography>
                )}
              </Box>
              <TemplatePreview
                key={selectedTemplate?.id}
                template={selectedTemplate}
                equipmentTypes={equipmentTypesResolvido}
              />
            </>
          ) : (
            <Box sx={modalStyles?.previewPlaceholder}>
              <Typography sx={modalStyles?.emptyText}>
                Selecione um template para visualizar.
              </Typography>
            </Box>
          )}
          {/* Aplicar num único circuito ou em vários: qual dos dois aparece é fixo pra vida do modal
          (decidido por `modo`, definido por quem abriu — DiagramaCanvas vs serviceItem.js — nunca
          muda com o modal aberto), então o botão do modo errado não fica desabilitado ocupando
          espaço à toa: some. `!selectedTemplate`/`!editMode` continuam desabilitando (podem mudar
          com o modal já aberto — selecionar um template, por exemplo), então esses seguem via disabled */}
          <Box sx={modalStyles?.aplicarBox}>
            {modo === "individual" && (
              <Button
                variant="contained"
                fullWidth
                onClick={handleAplicar}
                disabled={!selectedTemplate || !editMode}
              >
                Aplicar template
              </Button>
            )}
            {modo === "massa" && (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleAbrirAplicarEmMassa}
                disabled={!selectedTemplate}
              >
                Aplicar em vários circuitos
              </Button>
            )}
            {!selectedTemplate ? (
              <Typography sx={modalStyles?.aplicarHint}>
                Selecione um template para aplicar.
              </Typography>
            ) : modo === "massa" ? (
              <Typography sx={modalStyles?.aplicarHint}>
                Abra o modo de edição para aplicar num único circuito.
              </Typography>
            ) : (
              <Typography sx={modalStyles?.aplicarHint}>
                Saia do modo de edição para aplicar em vários circuitos.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Renomear/editar descrição reutiliza o modal de salvar em modo update */}
      <ModalSalvarTemplate
        open={Boolean(renameTarget)}
        onClose={() => setRenameTarget(null)}
        mode="update"
        initialData={renameTarget}
        setSnackbar={setSnackbar}
        onSaved={async () => {
          setRenameTarget(null);
          await carregarTemplates();
        }}
      />

      <InternoModalConfirmacaoSaida
        openConfirmacao={Boolean(deleteConfirm)}
        setOpenConfirmacao={(isOpen) => {
          if (!isOpen) setDeleteConfirm(null);
        }}
        title="Excluir template?"
        headerText={`Tem certeza que deseja excluir "${deleteConfirm?.nome || ""}"?`}
        contentText="O template será removido para todos os operadores. Diagramas que já o usaram não são afetados."
        confirmText="Excluir"
        execute={handleDelete}
        cancel={() => setDeleteConfirm(null)}
        severity="error"
      />
    </Dialog>

    {/* Fora da Dialog acima: precisa continuar montado quando ela fecha (Task 4) */}
    <ModalAplicarEmMassa
      aberto={aplicarEmMassaOpen}
      onFechar={() => setAplicarEmMassaOpen(false)}
      template={selectedTemplate}
      servicos={servicos}
      bloqueadoPorPendencia={bloqueadoPorPendencia}
      onAplicar={handleAplicarEmMassa}
    />
    </>
  );
}
